import { useState } from 'react';
import { Download, FileText, Image as ImageIcon, Loader2, Pencil } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { ClientPackagePdf } from '@/pdf/ClientPackagePdf';
import { PhotoUpload } from '@/components/PhotoUpload';
import { PresentationEditor } from '@/components/PresentationEditor';
import { PackageBlockGenerator } from '@/components/PackageBlockGenerator';
import { fetchPackagesByProject, fetchProject } from '@/lib/api';
import type { Client, GiftPackage, Project } from '@/lib/types';

interface Props {
  project: Project;
  pkg: GiftPackage;
  client: Client | null;
  /** Refresh parent na save (project+packages) */
  onChanged: () => void;
}

type Tab = 'tekst' | 'fotos';

function slug(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

async function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * PresentationFlow — Mark's workflow voor klant-PDF.
 * Tabs: tekst (AI + editor) en foto's. Onderaan altijd: "Genereer PDF" knop.
 *
 * Houdt eigen lokale state voor pkg en project zodat saves direct zichtbaar zijn
 * zonder full reload van de parent.
 */
export function PresentationFlow({
  project: initialProject,
  pkg: initialPkg,
  client,
  onChanged,
}: Props) {
  const [project, setProject] = useState(initialProject);
  const [pkg, setPkg] = useState(initialPkg);
  const [tab, setTab] = useState<Tab>('tekst');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reloadLocal() {
    try {
      const [p, pkgs] = await Promise.all([
        fetchProject(project.id),
        fetchPackagesByProject(project.id),
      ]);
      setProject(p);
      const refreshed = pkgs.find((x) => x.id === pkg.id);
      if (refreshed) setPkg(refreshed);
    } catch {
      // niet kritisch
    }
  }

  async function handleGeneratePdf() {
    setGenerating(true);
    setError(null);
    try {
      // Refresh local state first zodat we de laatste tekst en foto's pakken
      await reloadLocal();
      const blob = await pdf(
        <ClientPackagePdf project={project} pkg={pkg} client={client} />
      ).toBlob();
      const fname = `presentatie-${slug(project.name)}-${slug(client?.company_name ?? 'klant')}.pdf`;
      await downloadBlob(blob, fname);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PDF genereren mislukt');
    } finally {
      setGenerating(false);
    }
  }

  const photoCount = (pkg.photo_urls ?? []).length;
  const hasText = !!(project.client_presentation_text ?? '').trim();

  return (
    <div className="flex h-full flex-col">
      {/* Tab bar */}
      <div className="flex shrink-0 items-center gap-1 border-b border-bmade-200 pb-2 dark:border-bmade-800">
        <button
          type="button"
          onClick={() => setTab('tekst')}
          className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            tab === 'tekst'
              ? 'bg-accent-500 text-white'
              : 'text-bmade-600 hover:bg-bmade-100 dark:text-bmade-300 dark:hover:bg-bmade-800'
          }`}
        >
          <Pencil className="h-3 w-3" />
          Tekst {hasText && <span className="opacity-70">·</span>}
        </button>
        <button
          type="button"
          onClick={() => setTab('fotos')}
          className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            tab === 'fotos'
              ? 'bg-accent-500 text-white'
              : 'text-bmade-600 hover:bg-bmade-100 dark:text-bmade-300 dark:hover:bg-bmade-800'
          }`}
        >
          <ImageIcon className="h-3 w-3" />
          Foto's {photoCount > 0 && <span className="opacity-70">({photoCount})</span>}
        </button>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto py-4">
        {tab === 'tekst' ? (
          <div className="space-y-5">
            <PresentationEditor
              project={project}
              pkg={pkg}
              client={client}
              onSaved={() => void reloadLocal()}
            />
            <PackageBlockGenerator
              pkg={pkg}
              uitgangspunten={project.uitgangspunten ?? undefined}
              onPackageChanged={() => void reloadLocal()}
            />
          </div>
        ) : (
          <PhotoUpload
            packageId={pkg.id}
            photoUrls={pkg.photo_urls ?? []}
            onChange={(urls) => setPkg((p) => ({ ...p, photo_urls: urls }))}
          />
        )}
      </div>

      {/* Footer: PDF download */}
      <div className="shrink-0 space-y-2 border-t border-bmade-200 pt-3 dark:border-bmade-800">
        {error && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] text-bmade-500">
            {!hasText && photoCount === 0
              ? "Genereer tekst en voeg foto's toe voor een complete presentatie."
              : !hasText
                ? 'Tekst ontbreekt nog. PDF werkt maar leest leeg.'
                : photoCount === 0
                  ? "Geen foto's. PDF toont een placeholder."
                  : `Klaar voor download: ${photoCount} foto's + tekst.`}
          </div>
          <button
            type="button"
            onClick={() => void handleGeneratePdf()}
            disabled={generating}
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-40"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <FileText className="h-4 w-4" />
            Genereer PDF
          </button>
        </div>
      </div>
    </div>
  );
}
